import { useState, useEffect, FC } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { StarryBackground, StyledGrid, StyledButton, NebulaEffect, MenuContainer, HeaderContainer, TitleContainer, SubtitleContainer, ExitButton, Spacer } from './styled';
import axios, { AxiosError } from 'axios';
import LinearProgress from '@mui/material/LinearProgress';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';

const getWebServicesByRolesURL = process.env.REACT_APP_API_URL + 'api/WebServices/GetWebServicesByRoles';

const MORTALITY_DASHBOARD_URL =
    process.env.REACT_APP_MORTALITY_DASHBOARD_URL ?? 'http://192.168.67.9:5000/dashboard/mortality';

const menuItems = [
    {
        name: 'Мониторинг',
        icon: '📊',
        description: 'Система мониторинга',
        externalUrl: MORTALITY_DASHBOARD_URL,
    },
    { name: 'МедВид', icon: '🏥', description: 'Медицинские видео' },
    { name: 'Кадры', icon: '👥', description: 'Управление персоналом' },
    { name: 'ПрофОсмотр.Дети', icon: '👶', description: 'Детские осмотры' },
    { name: 'Справки.Дети', icon: '📋', description: 'Детские справки', url: '/certificates' },
    { name: 'Отчеты', icon: '📈', description: 'Отчетность', url: '/reporting' },
    { name: 'Регистр коморбидных', icon: '🛰️', description: 'Кабинет диспансерного наблюдения', url: '/dispensary' },
    { name: 'Опросы', icon: '📝', description: 'Анкетирование' },
    { name: 'Администрирование', icon: '⚙️', description: 'Настройки системы' },
];

const Main: FC<{ getFioAndOrganization: (value: string) => void }> = ({ getFioAndOrganization }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<
        | {
              id: number;
              systemName: string;
              displayName: string;
              description: string;
          }[]
        | null
    >(null);
    const [error, setError] = useState<string | {} | undefined>(undefined);

    const navigate = useNavigate();

    // Убираем скролл на уровне страницы
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    useEffect(() => {
        setIsLoading(true);

        try {
            axios
                .get(getWebServicesByRolesURL, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                })
                .then((data) => {
                    setData(data.data);
                    setIsLoading(false);
                })
                .catch((err) => {
                    if (err.response.status === 401) {
                        localStorage.clear();
                        getFioAndOrganization('');
                        setIsLoading(false);
                        navigate('/login');
                    }
                });
        } catch (err) {
            if (err && err instanceof AxiosError) setError(err);
            else if (err && err instanceof Error) setError(err.message);
        }
    }, []);

    const isUserCanSeeLargeFamilies = data?.some((elem) => elem.systemName === 'LargeFamilies');
    const isUserCanSeeCertificates = data?.some((elem) => elem.systemName === 'Certificates');

    const handleExit = () => {
        localStorage.clear();
        getFioAndOrganization('');
        navigate('/login');
    };

    return isLoading ? (
        <LinearProgress />
    ) : (
        <StarryBackground>
            <NebulaEffect />
            {error ? (
                <Typography mt={1} color='error'>
                    Не удалось загрузить доступные вам возможности системы
                </Typography>
            ) : (
                <MenuContainer position='relative' zIndex={10}>
                    <HeaderContainer>
                        <Spacer />

                        <TitleContainer>
                            <Typography 
                                color='white' 
                                variant='h1' 
                                sx={{
                                    fontSize: '48px',
                                    fontWeight: 'bold',
                                    marginRight: '36px',
                                    textShadow: `
                                        0 0 10px rgba(59, 130, 246, 0.5),
                                        0 0 20px rgba(59, 130, 246, 0.3),
                                        0 0 30px rgba(59, 130, 246, 0.2)
                                    `,
                                    animation: 'titleGlow 3s ease-in-out infinite',
                                    '@keyframes titleGlow': {
                                        '0%, 100%': {
                                            textShadow: `
                                                0 0 10px rgba(59, 130, 246, 0.5),
                                                0 0 20px rgba(59, 130, 246, 0.3),
                                                0 0 30px rgba(59, 130, 246, 0.2)
                                            `,
                                        },
                                        '50%': {
                                            textShadow: `
                                                0 0 15px rgba(59, 130, 246, 0.8),
                                                0 0 25px rgba(59, 130, 246, 0.5),
                                                0 0 35px rgba(59, 130, 246, 0.3)
                                            `,
                                        },
                                    },
                                }}
                            >
                                К
                                <IconButton 
                                    href='https://rmiac18.ru/' 
                                    target='_blank'
                                    sx={{ 
                                        padding: 0,
                                        marginBottom: '10px',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            transform: 'scale(1.1)',
                                            cursor: 'pointer',
                                        }
                                    }}
                                >
                                    <img src='/logo-cosmos.svg' alt='custom icon' style={{ width: 50, height: 50 }} />
                                </IconButton>
                                СМОС
                            </Typography>
                        </TitleContainer>

                        <ExitButton onClick={handleExit}>
                            Выход
                        </ExitButton>
                    </HeaderContainer>

                    <SubtitleContainer>
                        <Typography 
                            color='rgba(255, 255, 255, 1)' 
                            sx={{
                                fontSize: '14px',
                                lineHeight: 1.5,
                                textAlign: 'center',
                            }}
                        >
                            Выберите модуль для работы
                        </Typography>
                    </SubtitleContainer>

                    <StyledGrid>
                        {menuItems.map(({ name, icon, description, url, externalUrl }) => (
                            <StyledButton
                                key={name}
                                onClick={() => {
                                    if (externalUrl) {
                                        window.open(externalUrl, '_blank', 'noopener,noreferrer');
                                        return;
                                    }
                                    if (url === '/certificates' && isUserCanSeeCertificates) {
                                        navigate(url);
                                    }
                                    if (url === '/dispensary' && isUserCanSeeLargeFamilies) {
                                        navigate(url);
                                    }
                                    if (url === '/reporting') {
                                        navigate(url);
                                    }
                                }}
                            >
                                <Icon color='inherit' sx={{ width: '36px', height: '36px' }}>
                                    {icon}
                                </Icon>
                                <Typography 
                                    color='white' 
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: 600,
                                    }}
                                >
                                    {name}
                                </Typography>
                                <Typography 
                                    color='rgba(255, 255, 255, 1)' 
                                    sx={{
                                        fontSize: '14px',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {description}
                                </Typography>
                            </StyledButton>
                        ))}
                    </StyledGrid>

                    <Typography 
                        color='rgba(255, 255, 255, 1)' 
                        sx={{
                            fontSize: '14px',
                            textAlign: 'center',
                        }}
                    >
                        © 2025 КОмплексная Система Медицинского Обслуживания и Сопровождения
                    </Typography>
                </MenuContainer>
            )}
        </StarryBackground>
    );
};

export default Main;
